interface HighlightItem {
  keys: string[];
  type: 'glossary' | 'type' | 'type-value' | 'variable' | 'variable-value' | 'document' | string;
  id: string;
  name?: string; // Display name of the item (e.g., glossary item name)
  metadata?: {
    gameId?: string;
    itemType?: string; // 'glossary', 'document', 'type', 'variable', etc.
    [key: string]: any;
  };
}

interface HighlightedText {
  originalText: string;
  highlightedHTML: string;
  highlights: Array<{
    start: number;
    end: number;
    item: HighlightItem;
  }>;
}

class TextHighlightingService {
  private highlightItems: HighlightItem[] = [];
  
  // Add items to highlight
  addHighlightItems(items: HighlightItem[]): void {
    this.highlightItems = [...this.highlightItems, ...items];
  }
  
  // Process text and return highlighted version
  processText(text: string): HighlightedText {
    const highlights: Array<{start: number, end: number, item: HighlightItem}> = [];
    let highlightedHTML = text;
    
    // Find all matches for each highlight item
    this.highlightItems.forEach(item => {
      item.keys.forEach(key => {
        // Create a regex that properly handles multi-word phrases
        // For multi-word phrases, we need to ensure we match the entire phrase
        // and handle cases where the phrase might be at the start/end of text or surrounded by punctuation
        const escapedKey = this.escapeRegExp(key);
        
        // Use different regex patterns for single words vs multi-word phrases
        let regex: RegExp;
        if (key.includes(' ')) {
          // For multi-word phrases, use lookahead/lookbehind to ensure proper boundaries
          // This ensures we match "not so heavy" but not "not so heavyweight"
          regex = new RegExp(`(?<!\\w)${escapedKey}(?!\\w)`, 'gi');
        } else {
          // For single words, use word boundaries
          regex = new RegExp(`\\b${escapedKey}\\b`, 'gi');
        }
        
        
        let match;
        
        while ((match = regex.exec(text)) !== null) {
          highlights.push({
            start: match.index,
            end: match.index + match[0].length,
            item
          });
        }
      });
    });
    
    // Sort highlights by start position (descending) to avoid offset issues
    highlights.sort((a, b) => b.start - a.start);
    
    // Apply highlights to HTML
    highlights.forEach(highlight => {
      const before = highlightedHTML.substring(0, highlight.start);
      const match = highlightedHTML.substring(highlight.start, highlight.end);
      const after = highlightedHTML.substring(highlight.end);
      
      const highlightedMatch = `<span class="text-highlight" data-highlight-id="${highlight.item.id}" data-highlight-type="${highlight.item.type}" data-highlight-name="${highlight.item.name || ''}">${match}</span>`;
      
      highlightedHTML = before + highlightedMatch + after;
    });
    
    return {
      originalText: text,
      highlightedHTML,
      highlights
    };
  }
  
  // Remove specific highlights
  removeHighlight(itemId: string): void {
    this.highlightItems = this.highlightItems.filter(item => item.id !== itemId);
  }
  
  // Get item details for tooltip
  async getItemDetails(type: string, id: string): Promise<any> {
    // This would integrate with your existing data fetching
    // For now, return a mock implementation
    return {
      name: `Item ${id}`,
      type,
      id
    };
  }
  
  // Get all highlight items
  getHighlightItems(): HighlightItem[] {
    return [...this.highlightItems];
  }
  
  // Clear all highlights
  clearAllHighlights(): void {
    this.highlightItems = [];
  }
  
  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Export singleton instance
export const textHighlightingService = new TextHighlightingService();
export type { HighlightItem, HighlightedText };